import { Injectable } from '@angular/core';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { AdvancedSearchFilters } from '../../../../shared/models/advanced-search-filters.model';
import { ServiceModule } from '../../../service.module';

export interface SavedFilterView {
    name: string;
    filters: AdvancedSearchFilters;
    createdAt: string;
    organizationPath?: string; // Optional: to scope views to specific org/path
    isDefault?: boolean; // Optional: mark this view as default
}

@Injectable({
    providedIn: ServiceModule
})
export class SavedFilterViewsService {
    private readonly STORAGE_KEY = 'saved-filter-views';

    constructor(private localStorageService: LocalStorageService) {}

    /**
     * Get all saved filter views
     */
    getAllViews(): SavedFilterView[] {
        const stored = this.localStorageService.get(this.STORAGE_KEY);
        if (!stored) {
            return [];
        }
        try {
            return JSON.parse(stored);
        } catch (e) {
            console.error('Failed to parse saved filter views', e);
            return [];
        }
    }

    /**
     * Save a new filter view
     */
    saveView(name: string, filters: AdvancedSearchFilters, organizationPath?: string, isDefault?: boolean): boolean {
        const views = this.getAllViews();

        // If setting as default, clear any existing default for this org path
        if (isDefault) {
            views.forEach(v => {
                if (v.organizationPath === organizationPath) {
                    v.isDefault = false;
                }
            });
        }

        // Check if name already exists
        const existingIndex = views.findIndex(v => v.name === name);

        const newView: SavedFilterView = {
            name,
            filters: { ...filters },
            createdAt: new Date().toISOString(),
            organizationPath,
            isDefault: isDefault || false
        };

        if (existingIndex >= 0) {
            // Update existing view
            views[existingIndex] = newView;
        } else {
            // Add new view
            views.push(newView);
        }

        try {
            this.localStorageService.set(this.STORAGE_KEY, JSON.stringify(views));
            return true;
        } catch (e) {
            console.error('Failed to save filter view', e);
            return false;
        }
    }

    /**
     * Delete a saved filter view by name
     */
    deleteView(name: string): boolean {
        const views = this.getAllViews();
        const filtered = views.filter(v => v.name !== name);

        if (filtered.length === views.length) {
            return false; // View not found
        }

        try {
            this.localStorageService.set(this.STORAGE_KEY, JSON.stringify(filtered));
            return true;
        } catch (e) {
            console.error('Failed to delete filter view', e);
            return false;
        }
    }

    /**
     * Get a specific filter view by name
     */
    getView(name: string): SavedFilterView | null {
        const views = this.getAllViews();
        return views.find(v => v.name === name) || null;
    }

    /**
     * Check if a view name already exists
     */
    viewExists(name: string): boolean {
        return this.getAllViews().some(v => v.name === name);
    }

    /**
     * Get the default view (global, not path-specific)
     */
    getDefaultView(organizationPath?: string): SavedFilterView | null {
        const views = this.getAllViews();
        // If organizationPath is provided, try to find path-specific default first
        if (organizationPath) {
            const pathSpecificDefault = views.find(v => v.isDefault && v.organizationPath === organizationPath);
            if (pathSpecificDefault) {
                return pathSpecificDefault;
            }
        }
        // Otherwise return any global default (first one marked as default)
        return views.find(v => v.isDefault) || null;
    }
}
